import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { StandardEvents } from '@shopify/events';

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
const STRIDE = 28;
/* A full trip down the rail takes at least this many rails of scrolling.
   A one-screen page, like Projects, only walks a few steps. */
const RAIL_RUN = 3;
/* Every product list uses this stride, a filtered one included. */
const LIST_STRIDE = 56;
/* Cadence ceiling. A sprint is ~190 steps a minute: four poses a step,
   so no pose may hold for less than this. A flick of the wheel then reads
   as a sprint, not a strobe. */
const FRAME_MIN_MS = 55;
/* How much of the way to the target lean each frame moves. */
const LEAN_EASE = 0.3;
const IDLE_MS = 180;
const TRAVEL_MS = 700;
const JOIN_MS = 700;
/* Two strides behind the resting place, so the fall-in is two steps. */
const JOIN_DROP = STRIDE * 2;
/* An open run keeps the pack jogging on the spot: one pose per this many
   ms, scaled by each body's stride, so no two are in step. A pose every
   110ms is ~135 steps a minute: a jog, not the sprint of a flicked wheel. */
const JOG_MS = 110;
const JOG_LEAN = 2;
/* A run page folds open or shut over --motion-stretch and dissolves over
   --motion-stride (temple-motion.css). The ground under the lead changes
   as the page passes, so it is read again for that long. */
const FOLD_MS = 700 + 350;
const EXIT_KEY = 'temple-runner-exit';
const EXIT_MAX_AGE = 15000;
/* Below this width the page margin is 16px and the copy shares the left
   edge with the figure. It then keeps to the blank stretches. Matches the
   --runner-rail breakpoint in temple-runner.css. */
const NARROW = window.matchMedia('(max-width: 749px)');
/* Narrow homepage: each snapped screen has one spot, beside its title.
   The titles are inset 2.75rem for it (see .temple-runs__intro). The lead
   runs from spot to spot as the page snaps. The hero spot is the top of
   the rail. The page margin is 16px there (assets/base.css). */
const STOP_SELECTOR = '.temple-shop__title, .temple-runs__chapter, .temple-projects--landing .temple-projects__title';
const NARROW_MARGIN = 16;
const NARROW_INSET = 44;
/* Clearance kept between the figure and anything on the page, above and below. */
const CLEAR_PAD = 8;
/* How long the page read (every spot's extent) is trusted before the next
   frame reads it again. Mutation and resize observers refresh it sooner. */
const SPOTS_MAX_AGE = 2000;
/* How much of the way to a clear stretch each frame moves. */
const DODGE_EASE = 0.3;
/* A clear stretch further off than this many figure heights is not run to.
   The figure ducks out, crosses while hidden, and comes back. */
const HOP_SPAN = 2.5;
/* The duck-out fade: --motion-stride in temple-motion.css. */
const HOP_MS = 350;
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);
const MEDIA_SELECTOR = 'img, video, canvas, picture, iframe';
/* Things that take up room on the page besides a line of copy. */
const BOX_SELECTOR = `${MEDIA_SELECTOR}, svg, button, input, select, textarea, [role="button"]`;

/**
 * @typedef {Object} RunnerSpec
 * @property {number} frameOffset
 * @property {number} strideMul
 * @property {number} trail
 * @property {number} jitter
 * @property {number} scale
 */

/**
 * @typedef {Object} Runner
 * @property {HTMLElement} el
 * @property {number} distance
 * @property {number} frame
 * @property {number} homeFrame
 * @property {number} lastFrameAt
 * @property {number} strideMul
 * @property {number} trail
 * @property {number} jitter
 * @property {boolean} lead
 * @property {boolean} join
 */

/**
 * @typedef {Object} BandLayout
 * @property {HTMLElement[]} runs
 * @property {(Element | null)[]} titles
 * @property {boolean[]} light
 */

/**
 * @typedef {Object} FrameClock
 * @property {number} generation
 * @property {ReturnType<typeof setTimeout> | null} timer
 */

/**
 * Something on the page the figure must not stand over: a run of text,
 * or a box such as a photo or a button. clips are the ancestors that
 * cut it off, so a line scrolled out of a filmstrip does not count.
 * top / bottom are the element's document-space extent when the page
 * was read, so a move only measures what can be near the rail. pin is
 * the outermost fixed or sticky ancestor, if any: those travel with the
 * screen, so the pin is measured once a move and its spots only when it
 * is open and level with the rail. empty is a spot with no box when the
 * page was read (a closed panel); the page is read again when an open
 * or hidden attribute changes, so it is picked up once it shows.
 * @typedef {Object} Spot
 * @property {Element} el
 * @property {Range | null} range
 * @property {Element[]} clips
 * @property {number} top
 * @property {number} bottom
 * @property {Element | null} pin
 * @property {boolean} empty
 */

/**
 * What an element's ancestors do to it: the ones that clip it, and the
 * outermost one that is fixed or sticky.
 * @typedef {Object} Ancestry
 * @property {Element[]} clips
 * @property {Element | null} pin
 */

/** @type {Ancestry} */
const NO_ANCESTRY = { clips: [], pin: null };

/**
 * One screen of the narrow homepage: the scrollTop it snaps in at, and
 * where the feet rest on the rail once it has.
 * @typedef {Object} Stop
 * @property {number} scroll
 * @property {number} y
 */

/** The four who stay on a single run page. Offsets keep them out of step with the lead. */
const PACK_SPECS = [
  { frameOffset: 3, strideMul: 0.9, trail: 16, jitter: -10, scale: 0.94 },
  { frameOffset: 5, strideMul: 1.1, trail: 28, jitter: 8, scale: 0.9 },
  { frameOffset: 2, strideMul: 0.95, trail: 10, jitter: 2, scale: 0.96 },
  { frameOffset: 6, strideMul: 1.05, trail: 36, jitter: -4, scale: 0.88 },
];

/** Bodies that only exist so each Weekly Runs slab can have its own pack. */
const BAND_SPECS = [
  { frameOffset: 2, strideMul: 1, trail: 0, jitter: 0, scale: 0.94 },
  { frameOffset: 5, strideMul: 1, trail: 0, jitter: 0, scale: 0.9 },
  { frameOffset: 0, strideMul: 1, trail: 0, jitter: 0, scale: 0.96 },
  { frameOffset: 4, strideMul: 1, trail: 0, jitter: 0, scale: 0.88 },
  { frameOffset: 7, strideMul: 1, trail: 0, jitter: 0, scale: 0.92 },
  { frameOffset: 3, strideMul: 1, trail: 0, jitter: 0, scale: 0.9 },
];

/** How a cluster sits inside its slab: feet apart, a little out of line. */
const BAND_CLUSTER = [
  { trail: 0, jitter: -8, scale: 1 },
  { trail: 14, jitter: 8, scale: 0.94 },
  { trail: 26, jitter: -3, scale: 0.9 },
  { trail: 36, jitter: 4, scale: 0.86 },
];

const REAR_TRAIL = Math.max(...PACK_SPECS.map((spec) => spec.trail));

/** One stride behind that pack, then one stride apart. Same size. */
const JOIN_SPECS = [
  { frameOffset: 1, strideMul: 0.92, trail: REAR_TRAIL + STRIDE, jitter: 4, scale: 0.92 },
  { frameOffset: 4, strideMul: 1.08, trail: REAR_TRAIL + STRIDE * 2, jitter: -6, scale: 0.9 },
];

const PAUSE_SELECTOR = 'dialog[open], #Details-menu-drawer-container[open], header-menu[aria-expanded="true"]';

/**
 * Line runner in the left margin. Scroll distance is the stride, so a slow
 * scroll is a jog and a fast one is a sprint. Crossing from another page,
 * the lead turns and runs to that page's scroll position.
 */
class TempleRunner extends HTMLElement {
  /** @type {{ samples: number[] } | null} */
  #route = null;

  /** @type {EventTarget | null} */
  #target = null;

  /** @type {HTMLElement | null} */
  #lead = null;

  /** @type {MutationObserver | null} */
  #pauseObserver = null;

  #rafPending = false;
  #begun = false;
  #traveling = false;
  #travelGen = 0;
  #travelFrom = 0;
  #travelTo = 0;
  #travelStart = 0;
  #lastTop = 0;
  #lastTime = 0;
  #amp = 40;
  #steep = Infinity;
  #dir = 1;
  #lean = 0;
  #listKey = '';

  /** @type {BandLayout | null} */
  #bands = null;
  #runOpen = false;
  #joining = false;
  #joinGap = JOIN_DROP;

  /** @type {FrameClock} */
  #joinClock = { generation: 0, timer: null };

  /* Jogging on the spot while a run page is open. The generation retires
     a loop that was running before the page closed. */
  #jogging = false;
  #jogGen = 0;
  #groundGen = 0;

  /** @type {MutationObserver | null} */
  #foldObserver = null;

  /** @type {Runner[]} */
  #runners = [];

  /** @type {MutationObserver | null} */
  #openObserver = null;

  /** @type {MutationObserver | null} */
  #pageObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #idleTimer = null;

  /* Narrow screens: how far the lead stands from its scroll position to
     keep clear of the copy, and whether nothing on screen is clear. */
  #offset = 0;
  #blocked = false;
  #hideUntil = 0;

  /** @type {FrameClock} */
  #dodgeClock = { generation: 0, timer: null };

  /** @type {Spot[] | null} */
  #spots = null;

  /** When the spots were read; they are read again after SPOTS_MAX_AGE. */
  #spotsAt = 0;

  /** @type {Stop[] | null} */
  #stops = null;

  /** @type {ResizeObserver | null} */
  #pageSize = null;

  /* Listeners are on. Any page but home stays false on a phone until 750px. */
  #mounted = false;

  connectedCallback() {
    if (REDUCE.matches) return;

    /* Inner pages: he stays off on a phone. Crossing 750px starts him. */
    if (this.dataset.mobile === 'off') NARROW.addEventListener('change', this.#onShelf);
    if (this.#shelved()) return;

    this.#mount();
  }

  disconnectedCallback() {
    NARROW.removeEventListener('change', this.#onShelf);
    this.#standDown();
  }

  /**
   * Inner pages have no spare gutter on a phone, so the figure stays off
   * there. The homepage is the exception. A wider window still gets him.
   */
  #onShelf = () => {
    if (this.#shelved()) {
      this.#standDown();
      return;
    }

    if (!this.#mounted) this.#mount();
  };

  #shelved() {
    return this.dataset.mobile === 'off' && NARROW.matches;
  }

  #mount() {
    if (this.#mounted || REDUCE.matches) return;

    const data = document.getElementById('temple-route-data');

    try {
      this.#route = data?.textContent ? JSON.parse(data.textContent) : null;
    } catch {
      this.#route = null;
    }

    this.#lead = this.querySelector('.temple-runner__figure--lead');
    if (!this.#lead) return;

    this.#mounted = true;
    this.#bindRunners();
    this.#watchPause();
    this.#watchOpen();
    this.#watchPage();
    document.addEventListener(StandardEvents.collectionUpdate, this.#onResults);
    document.addEventListener(StandardEvents.searchUpdate, this.#onResults);
    document.addEventListener('shopify:section:load', this.#onSection);
    document.addEventListener('shopify:section:unload', this.#onSection);

    scrollContainerMediaQuery.addEventListener('change', this.#rebind);
    window.addEventListener('resize', this.#layout);
    window.addEventListener('pagehide', this.#remember);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#rebind();

    if (document.readyState === 'complete') this.#onPageShow();
  }

  #standDown() {
    if (!this.#mounted) return;

    this.#mounted = false;
    this.#rafPending = false;
    this.classList.remove('is-ready', 'is-idle', 'is-paused', 'is-covered');
    this.#unbind();
    this.#pauseObserver?.disconnect();
    this.#openObserver?.disconnect();
    this.#pageObserver?.disconnect();
    this.#foldObserver?.disconnect();
    this.#pageSize?.disconnect();
    document.removeEventListener('load', this.#onMediaLoad, true);
    this.#joining = false;
    this.#jogging = false;
    this.#jogGen += 1;
    this.#groundGen += 1;
    this.#traveling = false;
    this.#stop(this.#joinClock);
    this.#stop(this.#dodgeClock);
    this.#begun = false;
    this.#bands = null;
    this.#spots = null;
    this.#stops = null;
    document.removeEventListener(StandardEvents.collectionUpdate, this.#onResults);
    document.removeEventListener(StandardEvents.searchUpdate, this.#onResults);
    document.removeEventListener('shopify:section:load', this.#onSection);
    document.removeEventListener('shopify:section:unload', this.#onSection);
    scrollContainerMediaQuery.removeEventListener('change', this.#rebind);
    window.removeEventListener('resize', this.#layout);
    window.removeEventListener('pagehide', this.#remember);
    window.removeEventListener('pageshow', this.#onPageShow);

    if (this.#idleTimer) clearTimeout(this.#idleTimer);
  }

  #rebind = () => {
    this.#unbind();
    this.#target = getScrollEventTarget();
    this.#target.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#lastTop = getScrollContainer().scrollTop;
    this.#lastTime = performance.now();
    this.#layout();
  };

  #unbind() {
    this.#target?.removeEventListener('scroll', this.#onScroll);
    this.#target = null;
  }

  #onScroll = () => {
    if (this.#traveling) this.#traveling = false;
    if (this.#rafPending) return;

    this.#rafPending = true;
    requestAnimationFrame((time) => {
      if (!this.#rafPending) return;
      this.#tick(time);
    });

    /* If the frame never paints, the stride must still run and the lock must clear. */
    setTimeout(() => {
      if (!this.#rafPending) return;
      this.#tick(performance.now());
    }, 80);
  };

  /**
   * Scroll restoration writes scrollTop in a pageshow animation frame.
   * A timeout runs after that frame, including when the tab is in the background
   * and animation frames are paused.
   */
  #onPageShow = () => {
    if (this.#begun) return;
    this.#begun = true;
    setTimeout(() => this.#begin(), 0);
  };

  /** @param {Event} event */
  #onMediaLoad = (event) => {
    const { target } = event;

    if (!(target instanceof HTMLImageElement || target instanceof HTMLVideoElement)) return;
    if (this.contains(target)) return;

    this.#spots = null;
  };

  #layout = () => {
    this.#amp = this.#measure('--runner-amp');
    this.#steep = this.#steepSlope();
    this.#bands = null;
    this.#spots = null;
    this.#stops = null;

    if (!this.#begun || this.#traveling) return;

    this.#place(this.#progress(), true);
    this.#placePack();
    this.#matchGround();
    this.#matchChapter();
  };

  /* The theme editor swaps sections in place. Measure the slabs again. */
  #onSection = () => {
    this.#bands = null;
    this.#spots = null;
    this.#stops = null;
    this.#layout();
  };

  #begin() {
    if (this.#idleTimer) clearTimeout(this.#idleTimer);

    const target = this.#restY(this.#progress());
    const exit = readExit();
    const from = exit ? exit.ratio * this.clientHeight : target;
    const sameList = Boolean(exit?.path) && exit.path === window.location.pathname;
    const travel = Boolean(exit) && cameFromSite() && !sameList && Math.abs(from - target) > 24;

    this.#place(this.#progress(), true);
    this.#placePack();
    this.#matchGround();

    if (!travel || !this.#lead) {
      this.#matchChapter();
      /* Standing from the first frame, so an early open or resize eases. */
      this.#settle();
      this.#show();
      return;
    }

    delete this.dataset.chapter;
    this.#traveling = true;
    this.#travelFrom = from;
    this.#travelTo = target;
    this.#travelStart = performance.now();
    this.#dir = target >= from ? 1 : -1;
    this.classList.remove('is-idle');
    this.#lead.style.setProperty('--y', `${from}px`);
    this.#setFacing(this.#dir, 4);
    this.#show();
    this.#queueTravel();
  }

  #queueTravel() {
    const generation = (this.#travelGen += 1);

    const run = (time) => {
      if (generation !== this.#travelGen || !this.#traveling) return;

      this.#travelGen += 1;
      this.#travelFrame(time);
    };

    requestAnimationFrame(run);
    setTimeout(() => run(performance.now()), 32);
  }

  #travelFrame = (time) => {
    if (!this.#traveling || !this.#lead) return;

    const t = clamp((time - this.#travelStart) / TRAVEL_MS, 0, 1);
    const eased = easeOut(t);
    const y = this.#travelFrom + (this.#travelTo - this.#travelFrom) * eased;
    const previous = Number.parseFloat(this.#lead.style.getPropertyValue('--y')) || y;

    this.#lead.style.setProperty('--y', `${y}px`);
    this.#lead.style.setProperty('--x', `${this.#xAt(this.#progressAtY(y))}px`);
    this.#setFacing(this.#dir, 4);
    this.#step(this.#runners[0], Math.abs(y - previous), STRIDE);
    this.#placePack();
    this.#matchGround();

    if (t < 1) {
      this.#queueTravel();
      return;
    }

    this.#traveling = false;
    this.#dir = 1;
    this.#settle();
    this.#place(this.#progress(), true);
    this.#placePack();
    this.#matchChapter();
    this.#matchGround();
  };

  #bindRunners() {
    if (!this.#lead) return;

    this.#runners = [this.#runner(this.#lead, { frameOffset: 0, strideMul: 1, trail: 0, jitter: 0, scale: 1 }, false, true)];
    this.#bindGroup(
      this.querySelectorAll('.temple-runner__figure--pack:not(.temple-runner__figure--join):not(.temple-runner__figure--band)'),
      PACK_SPECS,
      false
    );
    this.#bindGroup(this.querySelectorAll('.temple-runner__figure--band'), BAND_SPECS, false);
    this.#bindGroup(this.querySelectorAll('.temple-runner__figure--join'), JOIN_SPECS, true);
  }

  /**
   * @param {NodeListOf<Element>} figures
   * @param {RunnerSpec[]} specs
   * @param {boolean} join
   */
  #bindGroup(figures, specs, join) {
    let index = 0;

    for (const el of figures) {
      const spec = specs[index];

      index += 1;
      if (!(el instanceof HTMLElement) || !spec) continue;

      el.style.setProperty('--scale', String(spec.scale));
      this.#runners.push(this.#runner(el, spec, join, false));
    }
  }

  /**
   * @param {HTMLElement} el
   * @param {RunnerSpec} spec
   * @param {boolean} join
   * @param {boolean} lead
   */
  #runner(el, spec, join, lead) {
    if (!lead) el.dataset.frame = String(spec.frameOffset);

    return {
      el,
      distance: 0,
      frame: spec.frameOffset,
      homeFrame: spec.frameOffset,
      lastFrameAt: 0,
      strideMul: spec.strideMul,
      trail: spec.trail,
      jitter: spec.jitter,
      lead,
      join,
    };
  }

  #watchPause() {
    this.#pauseObserver = new MutationObserver(() => this.#syncPause());
    this.#pauseObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open', 'aria-expanded'],
      subtree: true,
    });
    this.#syncPause();
  }

  #syncPause() {
    const paused = Boolean(document.querySelector(PAUSE_SELECTOR));

    this.classList.toggle('is-paused', paused);
    if (!paused) return;

    this.#traveling = false;
    if (this.#joining) {
      this.#finishJoin(this.#runOpen);
    }
    this.#settle();
  }

  #watchOpen() {
    this.#openObserver = new MutationObserver(this.#syncOpen);
    this.#openObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    this.#syncOpen();
  }

  /* Copy comes and goes (a project opens, a product list loads, a filter
     panel or drawer opens). Read the page again on the next move. The
     runner's own poses are not a change. */
  #watchPage() {
    this.#pageObserver = new MutationObserver((records) => {
      for (const record of records) {
        if (this.contains(record.target)) continue;

        this.#spots = null;
        this.#stops = null;
        return;
      }
    });
    this.#pageObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'hidden'],
    });

    /* The titles set the spots, and the page settles under them with no
       DOM change to see: fonts arrive, images size, the browser chrome
       folds away on the first scroll. Place again when the page's height
       changes, which is what each of those does. */
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#onSection();
    });
    this.#pageSize = new ResizeObserver(() => {
      /* The read-time extents in the spots are only as good as the
         layout they were read from. A taller or shorter page means
         something moved: read again on the next move, on every page. */
      this.#spots = null;
      if (this.#begun && !this.#traveling && this.#onStops()) this.#layout();
    });
    this.#pageSize.observe(document.body);

    /* An image or video arriving can resize its card without changing
       the page height (the grid rows are fixed): read again after each. */
    document.addEventListener('load', this.#onMediaLoad, true);

    /* A run page closing folds down past the lead long before the open
       class comes off (the script holds it until the dissolve ends). Watch
       the page's own classes so the ground is re-read through the fold. */
    const runs = document.querySelector('temple-weekly-runs');

    if (!runs) return;

    this.#foldObserver = new MutationObserver((records) => {
      for (const record of records) {
        const page = record.target;

        if (!(page instanceof HTMLElement) || !page.classList.contains('temple-runs__page')) continue;
        if (page.classList.contains('is-closing') && !page.classList.contains('is-fading')) {
          this.#trackGround(FOLD_MS);
          return;
        }
      }
    });
    this.#foldObserver.observe(runs, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  /**
   * Read the ground under the lead every frame for a while: a run page
   * folding over or away from it changes what it stands on mid-move.
   * @param {number} ms
   */
  #trackGround(ms) {
    const generation = (this.#groundGen += 1);
    const start = performance.now();

    /** @param {number} time */
    const frame = (time) => {
      if (generation !== this.#groundGen || !this.isConnected) return;

      this.#matchGround();

      if (time - start < ms) requestAnimationFrame(frame);
    };

    requestAnimationFrame(frame);
  }

  /**
   * While a run is open the pack jogs on the spot: poses turn over at a
   * jog cadence with nothing scrolling. Each body's stride scales the
   * cadence so the group is out of step, as on the road.
   */
  #startJog() {
    if (this.#jogging) return;

    this.#jogging = true;

    const generation = (this.#jogGen += 1);
    const lean = String(JOG_LEAN);

    for (const runner of this.#runners) {
      if (this.#holdJoin(runner)) continue;

      if (runner.el.dataset.frame === 'stand') runner.el.dataset.frame = String(runner.frame);
      runner.el.style.setProperty('--lean', lean);
    }

    /** @param {number} time */
    const frame = (time) => {
      if (generation !== this.#jogGen || !this.#jogging || !this.isConnected) return;

      for (const runner of this.#runners) {
        if (this.#holdJoin(runner)) continue;
        if (time - runner.lastFrameAt < JOG_MS * runner.strideMul) continue;

        runner.lastFrameAt = time;
        runner.frame = (runner.frame + 1) % 8;
        runner.el.dataset.frame = String(runner.frame);
        runner.el.style.setProperty('--lean', lean);
      }

      requestAnimationFrame(frame);
    };

    requestAnimationFrame(frame);
  }

  /* The page has closed: the pack pulls up and stands after the idle beat. */
  #stopJog() {
    if (!this.#jogging) return;

    this.#jogging = false;
    this.#jogGen += 1;
    this.#armIdle();
  }

  /**
   * On a single run page, opening a run brings two more in behind the
   * pack and closing peels them off. On the homepage the slab's own
   * pack simply moves up to the lead, so there is nothing to join.
   */
  #syncOpen = () => {
    const open = document.documentElement.classList.contains('temple-runs-page-open');

    if (open === this.#runOpen) return;

    this.#runOpen = open;

    if (this.dataset.bands === 'true') {
      this.#placePack();
    } else if (open) {
      this.#startJoin();
    } else {
      this.#endJoin();
    }

    if (open) this.#startJog();
    else this.#stopJog();

    /* The page folds over the lead through the next stretch; one read
       on the first paint would still see the slab it opened from. */
    this.#trackGround(FOLD_MS);
  };

  /**
   * The first of a paint and a short timeout wins, so a background tab
   * still finishes when animation frames are paused.
   * @param {FrameClock} clock
   * @param {(time: number) => void} run
   */
  #arm(clock, run) {
    const generation = (clock.generation += 1);

    const fire = (time) => {
      if (generation !== clock.generation || !this.isConnected) return;

      clock.generation += 1;
      if (clock.timer) clearTimeout(clock.timer);
      run(time);
    };

    requestAnimationFrame(fire);
    clock.timer = setTimeout(() => fire(performance.now()), 32);
  }

  /**
   * @param {FrameClock} clock
   */
  #stop(clock) {
    clock.generation += 1;
    if (!clock.timer) return;

    clearTimeout(clock.timer);
    clock.timer = null;
  }

  /**
   * @param {() => void} work
   */
  #onPaint(work) {
    requestAnimationFrame(work);
    setTimeout(work, 32);
  }

  #startJoin() {
    this.#stop(this.#joinClock);
    this.#joining = true;
    this.#joinGap = JOIN_DROP;

    const start = performance.now();

    for (const runner of this.#joiners()) {
      runner.distance = 0;
      runner.frame = runner.homeFrame;
      runner.el.dataset.frame = String(runner.homeFrame);
      runner.el.style.setProperty('--lean', '4');
    }

    this.#placePack();
    this.#arm(this.#joinClock, (time) => this.#joinFrame(time, start));
  }

  /**
   * @param {number} time
   * @param {number} start
   */
  #joinFrame(time, start) {
    if (!this.#joining || !this.#runOpen) return;

    const t = clamp((time - start) / JOIN_MS, 0, 1);
    const next = JOIN_DROP * (1 - easeOut(t));
    const moved = this.#joinGap - next;

    this.#joinGap = next;

    for (const runner of this.#joiners()) {
      this.#step(runner, moved, STRIDE);
    }

    this.#placePack();

    if (t < 1) {
      this.#arm(this.#joinClock, (nextTime) => this.#joinFrame(nextTime, start));
      return;
    }

    this.#finishJoin(true);
  }

  #endJoin() {
    this.#joining = false;
    this.#stop(this.#joinClock);
    this.#joinClock.timer = setTimeout(() => {
      if (this.#runOpen || !this.isConnected) return;

      this.#joinGap = JOIN_DROP;
      this.#placePack();
    }, JOIN_MS);
  }

  /**
   * Park the joiners. Resting puts them in the pack; dropping puts them
   * back behind it, where the next open starts.
   * @param {boolean} resting
   */
  #finishJoin(resting) {
    this.#joining = false;
    this.#stop(this.#joinClock);
    this.#joinGap = resting ? 0 : JOIN_DROP;

    for (const runner of this.#joiners()) {
      runner.distance = 0;

      /* Fallen in behind an open run, they jog with the rest. */
      if (resting && this.#jogging) {
        runner.el.style.setProperty('--lean', String(JOG_LEAN));
        continue;
      }

      runner.el.dataset.frame = 'stand';
      runner.el.style.setProperty('--lean', '0');
    }

    this.#placePack();
  }

  #joiners() {
    return this.#runners.filter((runner) => runner.join);
  }

  /**
   * The join clock owns these figures until they have fallen in.
   * @param {Runner} runner
   */
  #holdJoin(runner) {
    /* On the homepage each slab's whole cluster runs, joiners included. */
    if (this.dataset.bands === 'true') return false;

    return runner.join && (!this.#runOpen || this.#joining);
  }

  /**
   * Ink on a light ground, paper on a dark one. Sample the point under
   * the torso so the switch follows the figure, not the chapter name.
   *
   * A photo or video is not a ground to stand on. The figure ducks out
   * until the margin is clear again — on small screens the galleries
   * bleed to the screen edge and would swallow it. The homepage hero is
   * the one photo it stands on, and he stays paper there: the picture is
   * pointer-events none, so a sample reads the light page behind it and
   * would paint him ink on the photograph.
   */
  #matchGround() {
    if (!this.#lead) return;

    const box = this.#lead.getBoundingClientRect();
    const onHero = this.#overHero(box);

    if (onHero) {
      this.classList.toggle('is-covered', this.#blocked);
      if (!this.#blocked) delete this.dataset.ground;
      return;
    }

    const under = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
    const ground = under ? groundAt(under) : null;
    /* Blocked: on a narrow screen there is no clear stretch to stand in. */
    const covered = this.#blocked || Boolean(ground?.media);

    this.classList.toggle('is-covered', covered);
    if (covered || !ground) return;

    if (ground.light) this.dataset.ground = 'light';
    else delete this.dataset.ground;
  }

  /**
   * True when the torso is over the homepage hero photograph.
   * @param {DOMRect} box
   */
  #overHero(box) {
    if (this.dataset.stage !== 'home') return false;

    const hero = document.querySelector('.hero');

    if (!hero) return false;

    const heroBox = hero.getBoundingClientRect();
    const y = box.top + box.height / 2;

    return y >= heroBox.top && y <= heroBox.bottom;
  }

  /* The pack is the community: Weekly Runs, and the three run pages. */
  #matchChapter() {
    if (this.dataset.pack === 'true') {
      this.dataset.chapter = 'runs';
      return;
    }

    const header = document.getElementById('header-component');
    const onRuns =
      header?.getAttribute('data-landing-section') === 'runs' ||
      Boolean(document.querySelector('.temple-runs-section[data-current]'));

    if (onRuns) this.dataset.chapter = 'runs';
    else if (this.dataset.chapter === 'runs') delete this.dataset.chapter;
  }

  /**
   * Scroll sets the feet. On a narrow screen the copy shares the left
   * edge, so the lead stands off its scroll position in the nearest
   * blank stretch instead. snap lands there at once: a first paint, a
   * resize, the end of a trip across pages.
   * @param {number} progress
   * @param {boolean} [snap]
   */
  #place(progress, snap = false) {
    if (!this.#lead) return;

    const y = this.#restY(progress);
    const clear = this.#clearY(y, progress);
    const landed = this.#steer(clear === null ? 0 : clear - y, clear !== null, snap);

    /* Landing out of sight must not ease across the copy as it fades in. */
    if (landed) this.#lead.style.setProperty('transition', 'none');

    this.#lead.style.setProperty('--x', `${this.#xAt(progress)}px`);
    this.#lead.style.setProperty('--y', `${y + this.#offset}px`);

    if (!landed) return;

    this.#lead.getBoundingClientRect();
    this.#lead.style.removeProperty('transition');
  }

  /**
   * Move the stand-off towards where it should be. Visible, the figure
   * runs there. Too far, or nowhere to stand, it ducks out, and lands
   * where it should once it is out of sight. True on that landing.
   * @param {number} target
   * @param {boolean} clear
   * @param {boolean} snap
   */
  #steer(target, clear, snap) {
    if (snap) {
      this.#hideUntil = 0;
      this.#offset = target;
      this.#blocked = !clear;
      return false;
    }

    const now = performance.now();
    const figure = this.#lead?.offsetHeight || 0;
    const far = Math.abs(target - this.#offset) > figure * HOP_SPAN;

    if (!this.#blocked && (!clear || far)) {
      this.#blocked = true;
      this.#hideUntil = now + HOP_MS;
    }

    if (this.#blocked) {
      /* Still fading: hold the pose and the place. */
      if (now < this.#hideUntil) {
        this.#chase();
        return false;
      }

      if (!clear) return false;

      this.#offset = target;
      this.#blocked = false;
      return true;
    }

    const gap = target - this.#offset;

    if (Math.abs(gap) < 0.5) {
      this.#offset = target;
      return false;
    }

    this.#offset += gap * DODGE_EASE;
    this.#chase();
    return false;
  }

  /* Keep moving once the scroll has stopped, until the stand-off has settled. */
  #chase() {
    this.#arm(this.#dodgeClock, () => {
      if (!this.#begun || this.#traveling || !this.#lead) return;

      const before = Number.parseFloat(this.#lead.style.getPropertyValue('--y')) || 0;
      const wasBlocked = this.#blocked;

      this.#place(this.#progress());

      const moved = Number.parseFloat(this.#lead.style.getPropertyValue('--y')) - before;

      /* Running to the spot, not sliding. Settle again once there.
         Landing out of sight after a duck-out is not a stride. */
      if (Math.abs(moved) >= 0.5 && !this.#blocked && !wasBlocked) {
        this.#step(this.#runners[0], Math.abs(moved), STRIDE);
        this.classList.remove('is-idle');
        this.#armIdle();
      }

      this.#placePack();
      this.#matchGround();
    });
  }

  /**
   * Narrow screens only. The feet position to show: the scroll position
   * when the figure is clear there, otherwise the nearest blank stretch
   * on screen that fits it, or null when there is none.
   * @param {number} y
   * @param {number} progress
   */
  #clearY(y, progress) {
    if (!NARROW.matches || this.#onStops() || !this.#lead) return y;

    const figure = this.#lead.offsetHeight || 0;
    const need = figure + CLEAR_PAD * 2;
    const rail = this.clientHeight;

    if (!figure || rail < need) return y;

    const host = this.getBoundingClientRect();
    const half = (this.#lead.offsetWidth || 0) / 2;
    const x = host.left + this.#xAt(progress);
    /** @type {[number, number][]} */
    const edges = [...this.#taken(x - half - 2, x + half + 2, host), [rail, rail]];
    let best = null;
    let bestDistance = Infinity;
    let cursor = 0;

    for (const [start, end] of edges) {
      const gapTop = cursor;
      const gapBottom = start;

      cursor = Math.max(cursor, end);
      if (gapBottom - gapTop < need) continue;

      const feet = clamp(y, gapTop + CLEAR_PAD + figure, gapBottom - CLEAR_PAD);
      const distance = Math.abs(feet - y);

      if (distance < bestDistance) {
        best = feet;
        bestDistance = distance;
      }
    }

    return best;
  }

  /**
   * The stretches of the rail, top to bottom, that copy or a box takes up
   * between two screen x positions. Merged and in order.
   * @param {number} left
   * @param {number} right
   * @param {DOMRect} host
   * @returns {[number, number][]}
   */
  #taken(left, right, host) {
    /* The read-time extents go stale when the page settles with nothing
       for the observers to see. A fresh read every couple of seconds of
       movement is cheap and bounds how long a stale one can mislead. */
    if (this.#spots && performance.now() - this.#spotsAt > SPOTS_MAX_AGE) this.#spots = null;

    const spots = (this.#spots ??= this.#readPage());
    /** @type {[number, number][]} */
    const spans = [];
    /* Where the rail sits in the document right now. Anything whose
       read-time extent is more than a screen away cannot be on it: a
       product list has well over a thousand spots and measuring every
       one on every frame was the whole cost of a scroll on a phone. The
       page's height changing (images sizing, a panel opening) drops the
       read, so the extents never drift far. A spot with no box when read
       is still measured: it may have been shown by a class since. */
    const scrollTop = getScrollContainer().scrollTop;
    const slack = window.innerHeight;
    const nearTop = scrollTop + host.top - slack;
    const nearBottom = scrollTop + host.bottom + slack;
    /* Fixed and sticky roots, measured once a move. A closed drawer has
       no box; the header bar and the footer sit above and below the
       rail. None of their spots can be on it. */
    /** @type {Map<Element, boolean>} */
    const pinLevel = new Map();

    for (const spot of spots) {
      if (spot.pin) {
        let level = pinLevel.get(spot.pin);

        if (level === undefined) {
          const pinBox = spot.pin.getBoundingClientRect();

          level = (pinBox.width > 0 || pinBox.height > 0) && pinBox.bottom > host.top && pinBox.top < host.bottom;
          pinLevel.set(spot.pin, level);
        }

        if (!level || spot.empty) continue;
      } else if (!spot.empty && (spot.bottom < nearTop || spot.top > nearBottom)) {
        continue;
      }

      const box = spot.el.getBoundingClientRect();

      if (box.width === 0 && box.height === 0) continue;
      if (box.right <= left || box.left >= right) continue;
      if (box.bottom <= host.top || box.top >= host.bottom) continue;
      if (!isShown(spot.el)) continue;

      const rects = spot.range ? spot.range.getClientRects() : [box];

      for (const rect of rects) {
        const seen = clipRect(rect, spot.clips);

        if (!seen) continue;
        if (seen.right <= left || seen.left >= right) continue;
        if (seen.bottom <= host.top || seen.top >= host.bottom) continue;

        spans.push([Math.max(0, seen.top - host.top), Math.min(host.height, seen.bottom - host.top)]);
      }
    }

    spans.sort((a, b) => a[0] - b[0]);

    /** @type {[number, number][]} */
    const merged = [];

    for (const span of spans) {
      const last = merged[merged.length - 1];

      if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1]);
      else merged.push([span[0], span[1]]);
    }

    return merged;
  }

  /**
   * Everything on the page the figure must keep clear of: each run of
   * text, each photo, each button. Read once, then measured on every
   * move. The homepage hero is the one photo it stands on.
   * @returns {Spot[]}
   */
  #readPage() {
    /** @type {Spot[]} */
    const spots = [];
    /** @type {Map<Element, Ancestry>} */
    const ancestryOf = new Map();
    const home = this.dataset.stage === 'home';
    const scrollTop = getScrollContainer().scrollTop;

    this.#spotsAt = performance.now();

    /**
     * The clipping ancestors of an element, and the outermost one that is
     * fixed or sticky. One computed-style read per ancestor, shared.
     * @param {Element | null} el
     * @returns {Ancestry}
     */
    const ancestry = (el) => {
      if (!el || el === document.body) return NO_ANCESTRY;

      let known = ancestryOf.get(el);

      if (known) return known;

      const above = ancestry(el.parentElement);
      const style = getComputedStyle(el);
      const clipper = style.overflowX !== 'visible' || style.overflowY !== 'visible' || style.clipPath !== 'none';
      const pinnedSelf = style.position === 'fixed' || style.position === 'sticky';

      known = { clips: clipper ? [el, ...above.clips] : above.clips, pin: above.pin ?? (pinnedSelf ? el : null) };
      ancestryOf.set(el, known);

      return known;
    };

    /**
     * @param {Element} el
     * @param {Range | null} range
     * @param {Element} parent
     */
    const spot = (el, range, parent) => {
      const { clips, pin } = ancestry(parent);
      const box = el.getBoundingClientRect();

      spots.push({
        el,
        range,
        clips,
        top: box.top + scrollTop,
        bottom: box.bottom + scrollTop,
        pin,
        empty: box.width === 0 && box.height === 0,
      });
    };

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node instanceof Element) {
          if (node === this || SKIP_TAGS.has(node.tagName)) return NodeFilter.FILTER_REJECT;
          if (home && node.matches(MEDIA_SELECTOR) && node.closest('.hero')) return NodeFilter.FILTER_REJECT;
          /* A pill is a link with a border: its outline takes up room, not just its words. */
          if (!node.matches(BOX_SELECTOR) && !hasBorder(node)) return NodeFilter.FILTER_SKIP;

          return NodeFilter.FILTER_ACCEPT;
        }

        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      },
    });

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node instanceof Element) {
        if (node.parentElement) spot(node, null, node.parentElement);
        continue;
      }

      const parent = node.parentElement;

      if (!parent) continue;

      const range = document.createRange();

      range.selectNodeContents(node);
      spot(parent, range, parent);
    }

    return spots;
  }

  #tick = (time) => {
    this.#rafPending = false;
    if (this.#traveling) return;

    /* Sections settle as images and fonts arrive, with no DOM change to
       see. Four boxes a scroll is cheap: read the stops afresh each tick. */
    this.#stops = null;

    const top = getScrollContainer().scrollTop;
    const delta = top - this.#lastTop;
    const dt = Math.max(1, time - this.#lastTime);

    this.#lastTop = top;
    this.#lastTime = time;

    const progress = this.#progress();

    this.#place(progress);
    this.#placePack();
    this.#matchGround();
    this.#matchChapter();

    const listKey = this.#listKeyNow();
    const listShifted = listKey !== this.#listKey && this.#listKey !== '';

    this.#listKey = listKey;

    /* A new page of products moves scrollTop without the user running. */
    if (listShifted && Math.abs(delta) > 80) return;
    if (Math.abs(delta) < 1 || !this.#lead) return;

    this.#dir = delta > 0 ? 1 : -1;

    const slope = (this.#sampleAt(progress + 0.005) - this.#sampleAt(progress - 0.005)) / 0.01;
    const climb = slope * this.#dir;
    let stride = this.#hasList() ? LIST_STRIDE : STRIDE;

    if (climb < -0.4) stride *= 1.1;
    else if (climb > this.#steep) stride *= 0.9;

    /* Scroll speed arrives in jerks. Ease the lean towards it so the
       torso settles forward rather than twitching with every event. */
    const speed = Math.abs(delta) / dt;
    const target = clamp(clamp(speed * 4, 0, 6) + clamp(climb * 3, -3, 4), -3, 10);

    this.#lean += (target - this.#lean) * LEAN_EASE;

    const lean = String(round(this.#lean, 2));

    for (const runner of this.#runners) {
      if (this.#holdJoin(runner)) continue;

      this.#step(runner, Math.abs(delta), stride);
      runner.el.style.setProperty('--dir', String(this.#dir));
      runner.el.style.setProperty('--lean', lean);
    }

    this.#placePack();
    this.classList.remove('is-idle');
    this.#armIdle();
  };

  /**
   * One pose per stride of scroll, never faster than the cadence ceiling.
   * Distance owed beyond half a stride is dropped, so a hard flick gives
   * a quick step or two and not a backlog of poses after the page stops.
   * The ceiling is on the wall clock, not the frame timestamp: on a late
   * frame the timestamp lags the real moment and two poses can land
   * closer than the ceiling allows.
   * @param {Runner | undefined} runner
   * @param {number} moved
   * @param {number} baseStride
   */
  #step(runner, moved, baseStride) {
    if (!runner) return;

    const stride = baseStride * runner.strideMul;

    runner.distance = Math.min(runner.distance + moved, stride * 1.5);

    if (runner.distance < stride) return;

    const now = performance.now();

    if (now - runner.lastFrameAt < FRAME_MIN_MS) return;

    runner.distance -= stride;
    runner.lastFrameAt = now;
    runner.frame = (runner.frame + 1) % 8;
    runner.el.dataset.frame = String(runner.frame);
  }

  #placePack() {
    if (!this.#lead) return;

    if (this.#placeBands()) return;

    delete this.dataset.bands;

    const leadY = Number.parseFloat(this.#lead.style.getPropertyValue('--y')) || this.#restY(this.#progress());
    const figure = this.#lead.offsetHeight || 0;
    const limit = Math.max(figure, this.clientHeight);

    for (const runner of this.#runners) {
      if (runner.lead) continue;

      const drop = runner.join ? this.#joinGap : 0;
      const y = clamp(leadY - (runner.trail + drop) * this.#dir, figure, limit);
      const room = Math.max(0, (this.clientWidth - (this.#lead.offsetWidth || 0)) / 2 - 1);
      const jitter = this.dataset.stage === 'home' ? runner.jitter : clamp(runner.jitter, -room, room);
      const x = this.#xAt(this.#progressAtY(y)) + jitter;

      runner.el.style.setProperty('--x', `${x}px`);
      runner.el.style.setProperty('--y', `${y}px`);
      runner.el.style.setProperty('--dir', String(this.#dir));
    }
  }

  /**
   * Homepage Weekly Runs: one cluster on each slab. A slab off the screen
   * is dropped so the groups do not pile up at the edge. Opening a run
   * keeps that slab's cluster with the lead.
   */
  #placeBands() {
    if (this.dataset.stage !== 'home' || this.dataset.pack === 'true' || !this.#lead) return false;

    const layout = this.#bandLayout();

    if (layout.runs.length < 2) return false;

    this.dataset.bands = 'true';

    const { runs, titles, light } = layout;

    /** @type {Runner[]} */
    const packs = [];

    for (const runner of this.#runners) {
      if (!runner.lead) packs.push(runner);
    }

    const per = Math.max(1, Math.floor(packs.length / runs.length));
    const openIndex = this.#runOpen ? runs.findIndex((run) => run.classList.contains('is-open')) : -1;
    const host = this.getBoundingClientRect();
    const figure = this.#lead.offsetHeight || 0;
    const width = this.#lead.offsetWidth || figure * (40 / 64);
    const half = width / 2;
    const stack = BAND_CLUSTER[Math.min(per, BAND_CLUSTER.length) - 1]?.trail ?? 0;
    let index = 0;

    for (const runner of packs) {
      const band = Math.floor(index / per);
      const slot = index % per;
      const cluster = BAND_CLUSTER[slot % BAND_CLUSTER.length];

      index += 1;

      runner.el.style.setProperty('--band-i', String(band));
      runner.el.style.setProperty('--slot-i', String(slot));
      runner.el.style.setProperty('--scale', String(cluster.scale));

      if (band >= runs.length || (this.#runOpen && band !== openIndex)) {
        runner.el.classList.add('is-band-off');
        continue;
      }

      if (this.#runOpen) {
        /* With the lead, on the rail, the pack takes the rail's colour. */
        delete runner.el.dataset.ground;
        runner.el.classList.remove('is-band-off');
        this.#placeOnLead(runner, cluster.trail, cluster.jitter);
        continue;
      }

      const box = runs[band].getBoundingClientRect();
      const visibleTop = Math.max(box.top, host.top);
      const visibleBottom = Math.min(box.bottom, window.innerHeight);

      if (visibleBottom - visibleTop < figure * 0.65) {
        runner.el.classList.add('is-band-off');
        continue;
      }

      /* Centre the cluster in the visible part of the slab. When the slab
         is shorter than the cluster, close the gaps rather than spill. */
      const roomTop = visibleTop - host.top;
      const roomBottom = visibleBottom - host.top;
      const span = Math.max(0, roomBottom - (roomTop + figure));
      const fit = stack > span && stack > 0 ? span / stack : 1;
      const mid = (roomTop + roomBottom) / 2;
      const anchor = clamp(mid + (stack * fit) / 2, roomTop + figure, roomBottom);
      const y = clamp(anchor - cluster.trail * fit, roomTop + figure, roomBottom);
      const title = titles[band];
      const titleLeft = title ? title.getBoundingClientRect().left - host.left : this.clientWidth;
      const x = clamp(half + 4 + cluster.jitter, half + 2, Math.max(half + 2, titleLeft - half - 10));

      runner.el.dataset.ground = light[band] ? 'light' : 'dark';
      runner.el.classList.remove('is-band-off');
      runner.el.style.setProperty('--x', `${x}px`);
      runner.el.style.setProperty('--y', `${y}px`);
      runner.el.style.setProperty('--dir', String(this.#dir));
    }

    return true;
  }

  /**
   * The slabs do not move or recolour between layouts, so read them once.
   * The pack takes its colour from its slab, not from whatever happens to
   * be under its feet, so a light title can never turn it to ink.
   */
  #bandLayout() {
    if (this.#bands) return this.#bands;

    /** @type {HTMLElement[]} */
    const runs = [];

    for (const run of document.querySelectorAll('.temple-runs__run')) {
      if (run instanceof HTMLElement) runs.push(run);
    }

    this.#bands = {
      runs,
      titles: runs.map((run) => run.querySelector('.temple-runs__title')),
      light: runs.map((run) => {
        const parsed = parseRgb(getComputedStyle(run).backgroundColor);

        return Boolean(parsed) && parsed.a > 0.4 && luminance(parsed) > 0.55;
      }),
    };

    return this.#bands;
  }

  /**
   * @param {Runner} runner
   * @param {number} trail
   * @param {number} jitter
   */
  #placeOnLead(runner, trail, jitter) {
    if (!this.#lead) return;

    const leadY = Number.parseFloat(this.#lead.style.getPropertyValue('--y')) || this.#restY(this.#progress());
    const figure = this.#lead.offsetHeight || 0;
    const limit = Math.max(figure, this.clientHeight);
    const y = clamp(leadY - trail * this.#dir, figure, limit);
    const room = Math.max(0, (this.clientWidth - (runner.el.offsetWidth || 0)) / 2 - 1);
    const x = this.#xAt(this.#progressAtY(y)) + clamp(jitter, -room, room);

    runner.el.style.setProperty('--x', `${x}px`);
    runner.el.style.setProperty('--y', `${y}px`);
    runner.el.style.setProperty('--dir', String(this.#dir));
  }

  /**
   * @param {number} dir
   * @param {number} lean
   */
  #setFacing(dir, lean) {
    for (const runner of this.#runners) {
      runner.el.style.setProperty('--dir', String(dir));
      runner.el.style.setProperty('--lean', String(lean));
    }
  }

  #settle() {
    if (this.#traveling) return;

    this.#lean = 0;

    for (const runner of this.#runners) {
      if (runner.join && this.#joining) continue;

      runner.distance = 0;
      runner.el.style.setProperty('--dir', '1');

      /* An open run keeps them jogging; only the rail goes still. */
      if (this.#jogging) continue;

      runner.el.dataset.frame = 'stand';
      runner.el.style.setProperty('--lean', '0');
    }

    this.classList.add('is-idle');
  }

  #armIdle() {
    if (this.#idleTimer) clearTimeout(this.#idleTimer);

    this.#idleTimer = setTimeout(() => {
      this.#settle();
    }, IDLE_MS);
  }

  #show() {
    if (this.classList.contains('is-ready')) return;

    this.style.setProperty('transition', 'none');
    this.classList.add('is-ready');
    this.getBoundingClientRect();
    this.style.removeProperty('transition');
  }

  #remember = () => {
    const height = this.clientHeight || 1;
    const parsed = Number.parseFloat(this.#lead?.style.getPropertyValue('--y') || '');
    const y = Number.isFinite(parsed) ? parsed : this.#restY(this.#progress());

    try {
      sessionStorage.setItem(
        EXIT_KEY,
        JSON.stringify({
          ratio: clamp(y / height, 0, 1),
          t: Date.now(),
          path: window.location.pathname,
        })
      );
    } catch {
      /* sessionStorage can be blocked */
    }
  };

  #progress() {
    const el = getScrollContainer();
    const max = el.scrollHeight - el.clientHeight;

    if (max <= 0) return 0;

    const raw = clamp(el.scrollTop / max, 0, 1);

    if (this.#hasList()) return this.#listProgress(raw);

    const rail = this.clientHeight;
    const floor = rail * RAIL_RUN;

    if (!rail || max >= floor) return raw;

    return clamp(el.scrollTop / floor, 0, 1);
  }

  /**
   * A filter replaces the list. The event carries its own promise, which
   * resolves once the new grid is in the document. Land on that grid.
   * @param {Event} event
   */
  #onResults = (event) => {
    const promise = 'promise' in event ? event.promise : null;
    const align = () => {
      if (!this.isConnected || !this.#begun || this.#traveling) return;

      this.#lastTop = getScrollContainer().scrollTop;
      this.#listKey = this.#listKeyNow();
      this.#spots = null;
      this.#stops = null;
      this.#place(this.#progress(), true);
      this.#placePack();
      this.#matchGround();
    };
    const run = () => {
      align();
      requestAnimationFrame(align);
    };

    if (promise && typeof promise.then === 'function') promise.then(run, run);
    else run();
  };

  #hasList() {
    return Boolean(document.querySelector('.product-grid[data-last-page]'));
  }

  /**
   * A collection is several pages of one list. Map the figure across the
   * whole list so appending the next page does not pull it back up the rail.
   * @param {number} raw
   */
  #listProgress(raw) {
    const span = this.#listSpan();

    if (!span || span.loaded >= span.last) return raw;

    return clamp((span.first - 1 + raw * span.loaded) / span.last, 0, 1);
  }

  #listKeyNow() {
    const span = this.#listSpan();

    if (!span) return '';

    return `${span.first}:${span.loaded}:${span.last}`;
  }

  #listSpan() {
    const grid = document.querySelector('.product-grid[data-last-page]');

    if (!(grid instanceof HTMLElement)) return null;

    const last = Number(grid.dataset.lastPage);

    if (!last || last <= 1) return null;

    const cards = grid.querySelectorAll(':scope > [data-page]');

    if (!cards.length) return null;

    let first = Infinity;
    const seen = new Set();

    for (const card of cards) {
      const page = Number(card.getAttribute('data-page'));

      if (!page) continue;

      seen.add(page);
      if (page < first) first = page;
    }

    if (!seen.size || first === Infinity) return null;

    return { first, loaded: seen.size, last };
  }

  /**
   * Homepage uses the climb. Every other page is flat.
   * @param {number} progress
   */
  #sampleAt(progress) {
    if (this.dataset.stage !== 'home') return 0;

    const samples = this.#route?.samples ?? [0];
    const last = samples.length - 1;
    const index = clamp(progress, 0, 1) * last;
    const i = Math.floor(index);
    const j = Math.min(i + 1, last);
    const t = index - i;

    return samples[i] + (samples[j] - samples[i]) * t;
  }

  /**
   * Foot position across the rail. On the homepage the climb drifts right.
   * On other pages the figure stays centred in the gutter.
   * @param {number} progress
   */
  #xAt(progress) {
    if (this.dataset.stage !== 'home') return this.clientWidth / 2;
    /* Centred in the inset the titles leave for it. */
    if (this.#onStops()) return NARROW_MARGIN + NARROW_INSET / 2;

    const half = (this.#lead?.offsetWidth || 0) / 2;
    const drift = Math.min(this.#amp, 16);

    return half + 6 + this.#sampleAt(progress) * drift;
  }

  /* Narrow homepage: the lead keeps to its spots, not the route. */
  #onStops() {
    return NARROW.matches && this.dataset.stage === 'home';
  }

  /**
   * Where the feet rest for a scroll position. On the route, that is the
   * progress down the rail. On stops, it is the spot for the screen, and
   * the way between spots while the page snaps from one to the next.
   * @param {number} progress
   */
  #restY(progress) {
    if (!this.#onStops()) return this.#yAt(progress);

    return this.#stopY(getScrollContainer().scrollTop);
  }

  /**
   * @param {number} scrollTop
   */
  #stopY(scrollTop) {
    const stops = (this.#stops ??= this.#readStops());
    let prev = stops[0];

    if (!prev) return this.#yAt(this.#progress());
    if (scrollTop <= prev.scroll) return prev.y;

    for (const next of stops) {
      if (scrollTop > next.scroll) {
        prev = next;
        continue;
      }

      const span = next.scroll - prev.scroll;
      const t = span > 0 ? (scrollTop - prev.scroll) / span : 1;

      return prev.y + (next.y - prev.y) * t;
    }

    return prev.y;
  }

  /**
   * The hero spot is the top of the rail. Every other screen's spot is
   * beside its title: feet on the title's baseline, reached when the
   * screen's section has snapped in.
   */
  #readStops() {
    const figure = this.#lead?.offsetHeight || 0;
    const rail = this.clientHeight;
    const host = this.getBoundingClientRect();
    const scroller = getScrollContainer();
    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    /** @type {Stop[]} */
    const stops = [{ scroll: 0, y: this.#yAt(0) + CLEAR_PAD }];

    for (const title of document.querySelectorAll(STOP_SELECTOR)) {
      if (!(title instanceof HTMLElement) || this.contains(title)) continue;

      /* The layout box, not the painted one: a title slides up as it
         comes into view, and the spot is where it ends. display: none
         leaves no height. */
      const height = title.offsetHeight;

      if (height === 0) continue;

      const screen = title.closest('.shopify-section') ?? title;
      const margin = Number.parseFloat(getComputedStyle(screen).scrollMarginBlockStart) || 0;
      const scroll = clamp(layoutTop(screen) - margin, 0, maxScroll);
      /* The feet sit on the baseline; descenders hang a little below. */
      const feet = layoutTop(title) + height * 0.88 - scroll - host.top;

      stops.push({ scroll, y: clamp(feet, figure, rail) });
    }

    stops.sort((a, b) => a.scroll - b.scroll);

    return stops;
  }

  /**
   * @param {number} y
   */
  #progressAtY(y) {
    const figure = this.#lead?.offsetHeight || 0;
    const travel = Math.max(1, this.clientHeight - figure);

    return clamp((y - figure) / travel, 0, 1);
  }

  /**
   * Foot position down the rail. The figure stays fully inside it.
   * @param {number} progress
   */
  #yAt(progress) {
    const figure = this.#lead?.offsetHeight || 0;
    const travel = Math.max(0, this.clientHeight - figure);

    return figure + progress * travel;
  }

  #steepSlope() {
    if (this.dataset.stage !== 'home') return Infinity;

    const samples = this.#route?.samples ?? [];

    if (samples.length < 2) return Infinity;

    const step = 1 / (samples.length - 1);
    const slopes = [];

    for (let i = 1; i < samples.length; i++) {
      slopes.push(Math.abs((samples[i] - samples[i - 1]) / step));
    }

    slopes.sort((a, b) => a - b);

    return slopes[Math.min(slopes.length - 1, Math.floor(slopes.length * 0.85))];
  }

  /**
   * Custom properties in rem. A probe resolves them to pixels.
   * @param {string} name
   */
  #measure(name) {
    const probe = document.createElement('div');

    probe.style.cssText = `position:absolute;visibility:hidden;width:var(${name})`;
    this.appendChild(probe);

    const width = probe.getBoundingClientRect().width;

    probe.remove();

    return width;
  }
}

/**
 * The colour actually drawn. A solid gradient counts: an open Weekly Runs
 * page paints its run colour as a gradient and leaves the paper colour as
 * background-color, so the phone-chrome gap can stay white. Reading only
 * background-color turns the men ink on a grey slab.
 * @param {CSSStyleDeclaration} style
 */
function paintedFill(style) {
  const image = style.backgroundImage;

  if (image !== 'none' && !image.includes('url(')) {
    const fromImage = parseRgb(image);

    if (fromImage && fromImage.a > 0.4) return fromImage;
  }

  return parseRgb(style.backgroundColor);
}

/**
 * What the torso is over: the first solid colour up the tree is the
 * ground; an image or video on the way up means there is no ground.
 * @param {Element | null} start
 * @returns {{ light: boolean, media: boolean, node: Element | null }}
 */
function groundAt(start) {
  let node = start;

  while (node instanceof Element) {
    if (node.classList.contains('temple-runner')) break;

    if (
      node instanceof HTMLImageElement ||
      node instanceof HTMLVideoElement ||
      node instanceof HTMLPictureElement ||
      node instanceof HTMLCanvasElement
    ) {
      return { light: false, media: true, node };
    }

    const style = getComputedStyle(node);

    if (style.backgroundImage.includes('url(')) return { light: false, media: true, node };

    const parsed = paintedFill(style);

    if (parsed && parsed.a > 0.4) return { light: luminance(parsed) > 0.55, media: false, node };

    node = node.parentElement;
  }

  return { light: false, media: false, node: null };
}

/**
 * Does this element draw an outline? Only inline-level wrappers are
 * asked: a section with a hairline would otherwise swallow its whole slab.
 * @param {Element} el
 */
function hasBorder(el) {
  if (!el.matches('a, span, label, li, em, strong, small, b, i')) return false;

  const style = getComputedStyle(el);

  if (style.borderTopStyle === 'none' && style.borderBottomStyle === 'none') return false;

  return Number.parseFloat(style.borderTopWidth) > 0 || Number.parseFloat(style.borderBottomWidth) > 0;
}

/**
 * Painted at all: not display none, hidden, or faded right out.
 * Older browsers without checkVisibility count everything laid out.
 * @param {Element} el
 */
function isShown(el) {
  if (typeof el.checkVisibility !== 'function') return true;

  return el.checkVisibility({ visibilityProperty: true, opacityProperty: true });
}

/**
 * Top of an element's layout box in document coordinates, summed up the
 * offsetParent chain. Unlike getBoundingClientRect it ignores transforms,
 * so an element mid-animation measures where it will come to rest.
 * @param {Element} el
 */
function layoutTop(el) {
  let top = 0;
  /** @type {Element | null} */
  let node = el;

  while (node instanceof HTMLElement) {
    top += node.offsetTop;
    node = node.offsetParent;
  }

  return top;
}

/**
 * The part of a rect left after each clipping ancestor, or null when
 * none of it is on show.
 * @param {DOMRect} rect
 * @param {Element[]} clips
 * @returns {{ top: number, bottom: number, left: number, right: number } | null}
 */
function clipRect(rect, clips) {
  if (rect.width <= 0 || rect.height <= 0) return null;

  let { top, bottom, left, right } = rect;

  for (const clip of clips) {
    const box = clip.getBoundingClientRect();

    top = Math.max(top, box.top);
    bottom = Math.min(bottom, box.bottom);
    left = Math.max(left, box.left);
    right = Math.min(right, box.right);

    if (bottom <= top || right <= left) return null;
  }

  return { top, bottom, left, right };
}

/**
 * @param {string} color
 */
function parseRgb(color) {
  const match = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)/);

  if (!match) return null;

  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
    a: match[4] === undefined ? 1 : Number(match[4]),
  };
}

/**
 * @param {{ r: number, g: number, b: number }} color
 */
function luminance(color) {
  const channel = (value) => {
    const c = value / 255;

    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

function readExit() {
  try {
    const raw = sessionStorage.getItem(EXIT_KEY);

    if (!raw) return null;

    const data = JSON.parse(raw);

    if (typeof data.ratio !== 'number' || typeof data.t !== 'number') return null;
    if (Date.now() - data.t > EXIT_MAX_AGE) return null;

    return data;
  } catch {
    return null;
  }
}

function cameFromSite() {
  const navigation = performance.getEntriesByType('navigation')[0];
  const type = navigation && 'type' in navigation ? navigation.type : '';

  if (type === 'reload') return false;
  if (type === 'back_forward') return true;
  if (type !== 'navigate') return false;

  try {
    return Boolean(document.referrer) && new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

function easeOut(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {number} value
 * @param {number} decimals
 */
function round(value, decimals) {
  const factor = 10 ** decimals;

  return Math.round(value * factor) / factor;
}

if (!customElements.get('temple-runner')) {
  customElements.define('temple-runner', TempleRunner);
}
